import { api } from './client'

/**
 * F-067 (TASK-0321): client della governance del catalogo (`ProductsController`, `OperatorsController`,
 * `DepartmentsController`). Tutti gli endpoint qui sono riservati agli Admin: per gli altri il server
 * risponde 403. Dove serve il corpo di una risposta 409 (conferma di disattivazione, errori di
 * attivazione) si usa `fetch` diretto, perche' `api()` lo ridurrebbe a un messaggio.
 */
export type CatalogKind = 'products' | 'operators' | 'departments'

/** Una riga di "dove e' usato". `blocking` + `count > 0` = serve conferma per disattivare. */
export interface CatalogUsageRow {
  area: string
  label: string
  count: number
  blocking: boolean
}

export interface CatalogIssue {
  code: string
  message: string
}

export interface CatalogValidation {
  isValid: boolean
  errors: CatalogIssue[]
  warnings: CatalogIssue[]
}

export interface CatalogFieldChange {
  field: string
  before: string | null
  after: string | null
}

export interface CatalogVersion {
  /** F-142: id della versione, serve per approvarla. */
  id: number
  version: number
  validFrom: string
  createdAt: string
  createdByUserId: number | null
  /** Sempre null (colonna immutabile): la firma e' in `approvedBy`. */
  approvedByUserId: number | null
  /** F-142 (REQ-220-002): utente che ha approvato la versione, null se non ancora approvata. */
  approvedBy: number | null
  approvedAt: string | null
  approvalNote: string | null
  reason: string
  changes: CatalogFieldChange[]
  snapshot: Record<string, string | null>
}

/** Nomi italiani dei campi mostrati nelle differenze tra versioni. */
export const CATALOG_FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  slug: 'Slug',
  code: 'Codice',
  departmentId: 'Settore',
  categoryId: 'Categoria',
  subcategoryId: 'Sottocategoria',
  operatorId: 'Operatore',
  applicableCustomerType: 'Tipo cliente',
  description: 'Descrizione',
  basePrice: 'Prezzo base',
  commissionValue: 'Provvigione base',
  companyRevenue: 'Ricavo azienda',
  pricingType: 'Tipo di prezzo',
  sddAmount: 'Importo SDD',
  recurringMonthlyAmount: 'Importo mensile ricorrente',
  requiresApproval: 'Richiede approvazione',
  validFrom: 'Valido dal',
  validTo: 'Valido fino al',
  isActive: 'Attivo',
  logoUrl: 'Logo',
  website: 'Sito web',
  sortOrder: 'Ordine',
}

export function getUsages(kind: CatalogKind, id: number): Promise<CatalogUsageRow[]> {
  return api<CatalogUsageRow[]>(`/api/${kind}/${id}/usages`)
}

/** Solo prodotti e operatori. */
export function getValidation(kind: 'products' | 'operators', id: number): Promise<CatalogValidation> {
  return api<CatalogValidation>(`/api/${kind}/${id}/validation`)
}

export function getVersions(kind: CatalogKind, id: number): Promise<CatalogVersion[]> {
  return api<CatalogVersion[]>(`/api/${kind}/${id}/versions`)
}

/** Solo prodotti e operatori. Il clone nasce disattivo. Slug gia' usato: errore 400 con messaggio. */
export function cloneCatalogEntity(
  kind: 'products' | 'operators',
  id: number,
  body: { name: string; slug: string; code?: string },
): Promise<{ id: number }> {
  return api<{ id: number }>(`/api/${kind}/${id}/clone`, { method: 'POST', body: JSON.stringify(body) })
}

async function csrfHeaders(): Promise<Headers> {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (res.ok) {
    const data = await res.json() as { token: string }
    headers.set('X-XSRF-TOKEN', data.token)
  }
  return headers
}

async function errorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: string; title?: string }
  return body.error ?? body.title ?? `HTTP ${res.status}`
}

export type ActivateResult =
  | { ok: true; warnings: CatalogIssue[] }
  | { ok: false; errors: CatalogIssue[]; warnings: CatalogIssue[] }

/** Attiva solo se la verifica di configurazione passa; altrimenti restituisce gli errori leggibili. */
export async function activateCatalogEntity(kind: 'products' | 'operators', id: number): Promise<ActivateResult> {
  const res = await fetch(`/api/${kind}/${id}/activate`, {
    method: 'POST', credentials: 'include', headers: await csrfHeaders(),
  })
  if (res.ok) {
    const body = await res.json() as { warnings?: CatalogIssue[] }
    return { ok: true, warnings: body.warnings ?? [] }
  }
  if (res.status === 409) {
    const body = await res.json() as { errors?: CatalogIssue[]; warnings?: CatalogIssue[] }
    return { ok: false, errors: body.errors ?? [], warnings: body.warnings ?? [] }
  }
  throw new Error(await errorMessage(res))
}

export type DeactivateResult =
  | { status: 'done' }
  | { status: 'needs-confirm'; usages: CatalogUsageRow[] }

/** Disattiva. Con usi bloccanti il server risponde 409 con l'elenco: si chiede conferma e si richiama con `confirm`. */
export async function deactivateCatalogEntity(
  kind: CatalogKind, id: number, confirm: boolean,
): Promise<DeactivateResult> {
  const res = await fetch(`/api/${kind}/${id}${confirm ? '?confirm=true' : ''}`, {
    method: 'DELETE', credentials: 'include', headers: await csrfHeaders(),
  })
  if (res.ok) return { status: 'done' }
  if (res.status === 409) {
    const body = await res.json() as { usages?: CatalogUsageRow[] }
    return { status: 'needs-confirm', usages: body.usages ?? [] }
  }
  throw new Error(await errorMessage(res))
}

/** Testo italiano dell'elenco degli usi, per la richiesta di conferma. */
export function describeUsages(usages: CatalogUsageRow[]): string {
  return usages.filter(u => u.count > 0).map(u => `- ${u.label}: ${u.count}`).join('\n')
}

/** F-142 (REQ-220-002): una versione in attesa di approvazione. Solo i nomi dei campi cambiati, mai i valori. */
export interface PendingCatalogVersion {
  id: number
  entityType: 'Product' | 'Operator' | 'Department'
  entityId: number
  version: number
  validFrom: string
  createdAt: string
  createdByUserId: number | null
  reason: string
  changedFields: string[]
}

export function getPendingVersions(): Promise<PendingCatalogVersion[]> {
  return api<PendingCatalogVersion[]>('/api/catalog/versions/pending')
}

/**
 * Firma la versione (secondo Admin, diverso dall'autore). 409 se l'autore e' chi firma o se e' gia' approvata:
 * il messaggio italiano del server arriva nell'`Error`. L'approvazione NON sospende l'efficacia della versione.
 */
export function approveVersion(versionId: number, note?: string): Promise<{ versionId: number; approvedBy: number; approvedAt: string }> {
  return api(`/api/catalog/versions/${versionId}/approve`, {
    method: 'POST',
    body: JSON.stringify(note && note.trim() ? { note: note.trim() } : {}),
  })
}
